import { useState, useEffect, useMemo, useCallback } from 'react';
import CodigoDescuento from '../components/CodigoDescuento';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, Check, AlertCircle, Loader2, Ticket,
} from 'lucide-react';

import {
  fetchHorarios, postReserva, crearCheckoutSession, getMisBonos, getProfesorPrecios, ApiError, API_BASE,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  semanaActualISO, semanaOffset, fechasEntre, parseFecha, lunesDe, domingoDe, aISO,
  formateaDiaCorto, formateaRangoSemana, formateaFechaHora, formateaCodigoReserva,
  yaEmpezada, ahoraEnMadrid,
} from '../lib/fechas';
import { etiquetaSala } from '../lib/sala';
import { st as stTxt } from '../lib/solicitudesTxt';

// ----- Constantes -----
const FORMATOS = ['todos', 'presencial', 'online'];
const NIVELES = ['todos', 'principiante', 'intermedio', 'avanzado'];
const RX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FILTROS_INICIALES = { estilo: [], formato: 'todos', nivel: 'todos' };
// mejora-web F2: con ≤ este nº de plazas libres se avisa «Últimas X plazas».
const UMBRAL_ULTIMAS = 3;

function formatEuros(n) {
  const num = Number(n);
  if (Number.isInteger(num)) return `${num} €`;
  return `${num.toFixed(2).replace('.', ',')} €`;
}

function minutosEntre(hIni, hFin) {
  const [a, b] = hIni.split(':').map(Number);
  const [c, d] = hFin.split(':').map(Number);
  return (c * 60 + d) - (a * 60 + b);
}

// ============================================================================
// SelectorSemana
// ============================================================================
function SelectorSemana({ semana, onChange, onActual }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const rango = formateaRangoSemana(semana.desde, semana.hasta, lang);

  return (
    <div className="sem-selector">
      <button
        type="button"
        className="sem-nav"
        onClick={() => onChange(semanaOffset(semana, -1))}
        aria-label={t('reservas.semana_anterior')}
      >
        <ChevronLeft size={18} strokeWidth={1.5} />
      </button>
      <div className="sem-rango">
        <div className="sem-rango-label">{rango}</div>
        <button type="button" className="sem-hoy" onClick={onActual}>
          {t('reservas.ir_a_actual')}
        </button>
      </div>
      <button
        type="button"
        className="sem-nav"
        onClick={() => onChange(semanaOffset(semana, +1))}
        aria-label={t('reservas.semana_siguiente')}
      >
        <ChevronRight size={18} strokeWidth={1.5} />
      </button>
    </div>
  );
}

// ============================================================================
// SelectorDias (mejora-web F2): 7 días de la semana con nº de clases
// ============================================================================
function SelectorDias({ fechas, conteo, seleccionado, onSelect, hoy }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const fmtDia = useMemo(() => new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }), [lang]);

  return (
    <div className="dias-selector" role="tablist" aria-label={t('reservas.dias_label')}>
      {fechas.map((f) => {
        const n = conteo.get(f) || 0;
        const d = new Date(f + 'T00:00:00Z');
        const activo = f === seleccionado;
        return (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={activo}
            className={'dia-tab' + (activo ? ' is-active' : '') + (n === 0 ? ' is-vacio' : '') + (f === hoy ? ' is-hoy' : '')}
            onClick={() => onSelect(f)}
          >
            <span className="dia-tab-nombre">{fmtDia.format(d).replace('.', '')}</span>
            <span className="dia-tab-num">{d.getUTCDate()}</span>
            <span className="dia-tab-count">
              {n === 0 ? t('reservas.dia_sin_clases') : t('reservas.clases_dia', { count: n })}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// FiltrosClases (mejora-web F2): compactos — Estilo, Nivel, Modalidad
// ============================================================================
function FiltrosClases({ filtros, onChange, estilos }) {
  const { t } = useTranslation();
  const algunFiltroActivo =
    filtros.estilo.length > 0 || filtros.formato !== 'todos' || filtros.nivel !== 'todos';

  return (
    <div className="filtros-compactos">
      <label className="filtro-select">
        <span className="sr-only">{t('reservas.filtros.estilo')}</span>
        <select
          value={filtros.estilo[0] || ''}
          onChange={(e) => onChange({ ...filtros, estilo: e.target.value ? [e.target.value] : [] })}
        >
          <option value="">{t('reservas.filtros.estilo_todos')}</option>
          {estilos.map((e) => (
            <option key={e.valor} value={e.valor}>{e.nombre}</option>
          ))}
        </select>
      </label>
      <label className="filtro-select">
        <span className="sr-only">{t('reservas.filtros.nivel')}</span>
        <select value={filtros.nivel} onChange={(e) => onChange({ ...filtros, nivel: e.target.value })}>
          {NIVELES.map((n) => (
            <option key={n} value={n}>
              {n === 'todos' ? t('reservas.filtros.nivel_todos') : t(`reservas.filtros.${n}`)}
            </option>
          ))}
        </select>
      </label>
      <div className="filtro-segmento" role="group" aria-label={t('reservas.filtros.formato')}>
        {FORMATOS.map((f) => (
          <button
            key={f}
            type="button"
            className={'filtro-seg-btn' + (filtros.formato === f ? ' active' : '')}
            onClick={() => onChange({ ...filtros, formato: f })}
          >
            {f === 'todos' ? t('reservas.filtros.todos') : t(`reservas.filtros.${f}`)}
          </button>
        ))}
      </div>
      {algunFiltroActivo && (
        <button type="button" className="filtros-limpiar" onClick={() => onChange(FILTROS_INICIALES)}>
          {t('reservas.filtros.limpiar')}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// SaldoBono (mejora-web F2): saldo del bono si hay sesión iniciada
// ============================================================================
function SaldoBono() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [estado, setEstado] = useState(null); // null = sin cargar / sin sesión

  useEffect(() => {
    if (!isAuthenticated || user?.rol === 'profesor') { setEstado(null); return undefined; }
    let cancelled = false;
    const token = localStorage.getItem('idoyoga_auth_token');
    if (!token) return undefined;
    getMisBonos(token, lang)
      .then((res) => { if (!cancelled) setEstado({ activos: res.activos || [] }); })
      .catch(() => { /* best-effort: sin saldo no se muestra nada */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.rol, lang]);

  if (!estado) return null;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short' }) : '');
  const membresia = estado.activos.find((b) => b.es_membresia);
  const bonos = estado.activos.filter((b) => !b.es_membresia);
  const creditos = bonos.reduce((a, b) => a + (b.creditos_restantes || 0), 0);

  let texto;
  if (membresia) {
    texto = t('reservas.saldo.membresia', { nombre: membresia.nombre, fecha: fmt(membresia.fecha_caducidad) });
  } else if (bonos.length > 0) {
    const proximo = bonos.map((b) => b.fecha_caducidad).filter(Boolean).sort()[0];
    texto = t('reservas.saldo.creditos', { count: creditos }) +
      (proximo ? ' · ' + t('reservas.saldo.caduca', { fecha: fmt(proximo) }) : '');
  } else {
    texto = t('reservas.saldo.sin_bono');
  }

  return (
    <div className="saldo-bono">
      <Ticket size={16} strokeWidth={1.5} />
      <span>{texto}</span>
      {membresia || bonos.length ? (
        <Link to="/mis-bonos" className="saldo-bono-link">{t('reservas.saldo.ver')}</Link>
      ) : (
        <a href="/#bonos" className="saldo-bono-link">{t('reservas.saldo.ver_bonos')}</a>
      )}
    </div>
  );
}

// ============================================================================
// FilaClase (mejora-web F2): hora+duración | clase, profesora, nivel,
// modalidad | plazas + barra | Reservar
// ============================================================================
function FilaClase({ instancia, onReservar, descuentoProfesor, esTitular }) {
  const { t } = useTranslation();
  const libres = instancia.plazas_libres ?? null;
  const total = instancia.aforo_maximo ?? null;
  const completa = libres === 0;
  const reservable = instancia.permite_reserva_online && !esTitular;
  const ultimas = libres !== null && libres > 0 && libres <= UMBRAL_ULTIMAS;
  const pctOcupado = total ? Math.round(((total - libres) / total) * 100) : 0;
  const profPrincipal = instancia.profesor?.nombre_publico;
  const tieneAlternativos = instancia.profesores_alternativos?.length > 0;
  const tieneDescuento = descuentoProfesor && descuentoProfesor.descuento_pct > 0;
  const duracion = instancia.duracion_minutos || minutosEntre(instancia.hora_inicio, instancia.hora_fin);
  const nivel = instancia.clase_tipo.nivel;

  return (
    <article className={'fila-clase' + (reservable ? '' : ' is-disabled')}>
      <div className="fc-hora">
        <span className="fc-hora-inicio">{instancia.hora_inicio}</span>
        <span className="fc-duracion">{t('clases.duracion_min', { min: duracion })}</span>
      </div>

      <div className="fc-info">
        <h3 className="fc-titulo">{instancia.clase_tipo.nombre}</h3>
        <div className="fc-sub">
          {profPrincipal && (
            <span className="fc-prof">
              {tieneAlternativos
                ? t('reservas.con_profesores_alt', { principal: profPrincipal })
                : t('reservas.con_profesor', { nombre: profPrincipal })}
            </span>
          )}
          <span className={'clase-nivel clase-nivel--' + nivel}>{t('clases.nivel_' + nivel)}</span>
          <span className={'fc-modalidad fc-modalidad--' + instancia.formato}>
            {t('reservas.formato_label.' + instancia.formato)}
            {instancia.sala && <> · {etiquetaSala(instancia.sala, t)}</>}
          </span>
        </div>
        {tieneDescuento && (
          <div className="tc-precio-prof">
            <span className="tc-precio-publico">{formatEuros(descuentoProfesor.precio_publico)}</span>
            <span className="tc-precio-prof-final">{formatEuros(descuentoProfesor.precio_profesor)}</span>
            <span className="tc-precio-prof-pct">−{descuentoProfesor.descuento_pct}%</span>
          </div>
        )}
      </div>

      <div className="fc-plazas">
        {libres !== null && total !== null ? (
          <>
            <span className={'fc-plazas-texto' + (ultimas ? ' is-ultimas' : '') + (completa ? ' is-completa' : '')}>
              {completa
                ? t('reservas.completa')
                : ultimas
                  ? t('reservas.ultimas_plazas', { count: libres })
                  : t('reservas.plazas_libres', { libres, total })}
            </span>
            <span className="fc-barra" aria-hidden="true">
              <span
                className={'fc-barra-fill' + (ultimas || completa ? ' is-ultimas' : '')}
                style={{ width: `${pctOcupado}%` }}
              />
            </span>
          </>
        ) : (
          <span className="fc-plazas-texto">{t('reservas.disponibilidad.' + instancia.color_disponibilidad)}</span>
        )}
      </div>

      <div className="fc-cta">
        {esTitular ? (
          <span className="tc-completa" title={t('reservas.titular_tooltip')}>
            {t('reservas.titular_eres')}
          </span>
        ) : reservable ? (
          <button type="button" className="btn-primary fc-btn" onClick={() => onReservar(instancia)}>
            {t('reservas.btn_reservar')}
          </button>
        ) : (
          <span className="tc-completa">
            {completa ? t('reservas.completa') : t('reservas.cerrada')}
          </span>
        )}
      </div>
    </article>
  );
}

// ============================================================================
// ListaDia (mejora-web F2): clases del día seleccionado
// ============================================================================
function ListaDia({ fecha, instancias, onReservar, preciosProfesor, profesorPropioId, hayFiltros }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();

  return (
    <section className="lista-dia">
      <h2 className="dia-heading">{formateaDiaCorto(fecha, lang)}</h2>
      {instancias.length === 0 ? (
        <div className="dia-vacio">
          {hayFiltros ? t('reservas.sin_resultados_dia') : t('reservas.sin_clases_dia')}
        </div>
      ) : (
        <div className="filas-clase">
          {instancias.map((it) => {
            const desc = preciosProfesor?.get(it.clase_tipo.id) || null;
            const esTitular = !!(profesorPropioId && it.profesor?.id === profesorPropioId);
            return (
              <FilaClase
                key={it.horario_id + '|' + it.fecha_clase}
                instancia={it}
                onReservar={onReservar}
                descuentoProfesor={desc}
                esTitular={esTitular}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// ModalReserva: form + envío + pantalla de confirmación post-éxito
// ============================================================================
const ESTADO_FORM = 'form';
const ESTADO_ENVIANDO = 'enviando';
const ESTADO_CONFIRMACION = 'confirmacion';

function ModalReserva({ instancia, onClose, onSuccess }) {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [estado, setEstado] = useState(ESTADO_FORM);
  const [form, setForm] = useState(() => ({
    nombre: user ? [user.nombre, user.apellidos].filter(Boolean).join(' ').trim() : '',
    email: user?.email || '',
    telefono: user?.telefono || '',
  }));
  const [errorCampo, setErrorCampo] = useState({});
  const [errorGeneral, setErrorGeneral] = useState(null);
  const [reservaConfirmada, setReservaConfirmada] = useState(null);
  const [codigoSel, setCodigoSel] = useState(null);
  const [bonoUsable, setBonoUsable] = useState(null);  // {id, nombre, creditos_restantes, es_membresia} o null

  // Bloqueo scroll del body mientras el modal está abierto
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Si está logado, cargamos su saldo de bonos para ofrecer "Pagar con bono"
  // como opción. Best-effort: si falla, solo se ocultan los bonos (resto del
  // modal sigue funcionando con online_stripe / en_estudio).
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const token = localStorage.getItem('idoyoga_auth_token');
    if (!token) return;
    getMisBonos(token, lang)
      .then((res) => {
        if (cancelled) return;
        // Primer bono activo con créditos disponibles. Membresías son ilimitadas.
        const usable = (res.activos || []).find((b) =>
          b.vigente && (b.es_membresia || b.creditos_restantes > 0)
        );
        if (usable) setBonoUsable(usable);
      })
      .catch(() => { /* silencio: el modal sigue funcionando sin bonos */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, lang]);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Precio mostrado de la clase suelta. El cobro real lo calcula el backend
  // (lib/precios.js → bonos_tipos.tipo='clase_suelta', hoy 20 €).
  const importeTexto = formatEuros(20);

  const validar = () => {
    const errs = {};
    if (!form.nombre.trim()) errs.nombre = t('modal.errores.nombre_requerido');
    if (!RX_EMAIL.test(form.email)) errs.email = t('modal.errores.email_invalido');
    if (!form.telefono.trim()) errs.telefono = t('modal.errores.telefono_requerido');
    setErrorCampo(errs);
    return Object.keys(errs).length === 0;
  };

  // metodo: 'en_estudio' o 'online_stripe'.
  // Para online_stripe, tras crear la reserva pedimos checkout-session y
  // redirigimos a la URL de Stripe.
  const submit = async (e, metodo) => {
    e?.preventDefault();
    setErrorGeneral(null);
    if (!validar()) return;
    setEstado(ESTADO_ENVIANDO);
    try {
      const token = isAuthenticated ? localStorage.getItem('idoyoga_auth_token') : null;
      const payload = {
        horario_id: instancia.horario_id,
        fecha_clase: instancia.fecha_clase,
        metodo_pago: metodo,
      };
      if (codigoSel?.codigo && metodo !== 'bono') payload.codigo_descuento = codigoSel.codigo;
      // Si está logado, mandamos token y omitimos guest. Si no, mandamos guest.
      if (!token) {
        payload.guest = {
          nombre: form.nombre.trim(),
          email: form.email.trim(),
          telefono: form.telefono.trim(),
        };
      }
      const reserva = await postReserva(payload, token, lang);

      if (metodo === 'online_stripe' && !reserva.gratuito) {
        const session = await crearCheckoutSession(reserva.codigo_reserva, form.email.trim());
        if (session?.url) {
          // Cacheamos el email en session para auto-rellenar /reserva/:codigo
          // tras el redirect. Mejor UX que re-pedirlo.
          try {
            sessionStorage.setItem(`idy_reserva_email_${reserva.codigo_reserva}`, form.email.trim());
          } catch (_) { /* incognito modes pueden bloquearlo */ }
          window.location.href = session.url;
          return;
        }
        throw new Error('Sin URL de checkout');
      }

      setReservaConfirmada(reserva);
      setEstado(ESTADO_CONFIRMACION);
    } catch (err) {
      setEstado(ESTADO_FORM);
      if (err instanceof ApiError && err.code) {
        const traduccion = t('modal.errores.' + err.code, { defaultValue: '' });
        setErrorGeneral(traduccion || err.message || t('modal.errores.generico'));
      } else {
        setErrorGeneral(t('modal.errores.generico'));
      }
    }
  };

  const cerrar = () => {
    if (estado === ESTADO_CONFIRMACION) onSuccess(reservaConfirmada);
    onClose();
  };

  // Render
  return createPortal(
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={cerrar}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={cerrar} aria-label={t('comun.cerrar')}>
          <X size={20} strokeWidth={1.5} />
        </button>

        {estado === ESTADO_CONFIRMACION ? (
          <PantallaConfirmacion
            reserva={reservaConfirmada}
            instancia={instancia}
            onClose={cerrar}
            lang={lang}
          />
        ) : (
          <form className="modal-form" onSubmit={(e) => submit(e, 'en_estudio')} noValidate>
            <h2 className="modal-titulo">{t('modal.titulo')}</h2>

            <div className="modal-resumen">
              <div className="modal-resumen-titulo">{instancia.clase_tipo.nombre}</div>
              <div className="modal-resumen-linea">
                {formateaFechaHora(instancia.fecha_clase, instancia.hora_inicio, lang)}
              </div>
              {instancia.profesor && (
                <div className="modal-resumen-linea modal-resumen-sub">
                  {t('reservas.con_profesor', { nombre: instancia.profesor.nombre_publico })}
                </div>
              )}
              <div className="modal-resumen-importe">
                {t('modal.importe_label')}: <strong>{importeTexto}</strong>
              </div>
            </div>

            {isAuthenticated && (
              <p className="modal-prefill-aviso">
                {t('modal.aviso_logado', { nombre: user?.nombre || user?.email })}
              </p>
            )}

            {bonoUsable && (
              <p className="modal-bono-aviso">
                {bonoUsable.es_membresia
                  ? t('modal.aviso_bono_membresia', { nombre: bonoUsable.nombre })
                  : t('modal.aviso_bono', { creditos: bonoUsable.creditos_restantes })}
              </p>
            )}

            <div className="modal-campo">
              <label htmlFor="r-nombre">{t('modal.form.nombre')}</label>
              <input
                id="r-nombre"
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder={t('modal.form.nombre_placeholder')}
                aria-invalid={!!errorCampo.nombre}
                disabled={estado === ESTADO_ENVIANDO}
              />
              {errorCampo.nombre && <div className="modal-error-campo">{errorCampo.nombre}</div>}
            </div>

            <div className="modal-campo">
              <label htmlFor="r-email">{t('modal.form.email')}</label>
              <input
                id="r-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t('modal.form.email_placeholder')}
                aria-invalid={!!errorCampo.email}
                disabled={estado === ESTADO_ENVIANDO}
              />
              {errorCampo.email && <div className="modal-error-campo">{errorCampo.email}</div>}
            </div>

            <div className="modal-campo">
              <label htmlFor="r-tel">{t('modal.form.telefono')}</label>
              <input
                id="r-tel"
                type="tel"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder={t('modal.form.telefono_placeholder')}
                aria-invalid={!!errorCampo.telefono}
                disabled={estado === ESTADO_ENVIANDO}
              />
              {errorCampo.telefono && <div className="modal-error-campo">{errorCampo.telefono}</div>}
            </div>

            <CodigoDescuento
              ambito="clases"
              params={{ horario_id: instancia.horario_id }}
              email={form.email.trim()}
              token={isAuthenticated ? localStorage.getItem('idoyoga_auth_token') : null}
              onChange={setCodigoSel}
              disabled={estado === ESTADO_ENVIANDO}
            />

            <p className="modal-aviso">{t('modal.form.aviso_marketing')}</p>

            {errorGeneral && (
              <div className="modal-error-general">
                <AlertCircle size={16} strokeWidth={1.75} />
                <span>{errorGeneral}</span>
              </div>
            )}

            <div className="modal-acciones">
              {bonoUsable && (
                <button
                  type="button"
                  className="btn-primary modal-btn-principal modal-btn-bono"
                  onClick={(e) => submit(e, 'bono')}
                  disabled={estado === ESTADO_ENVIANDO}
                >
                  {estado === ESTADO_ENVIANDO ? (
                    <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('modal.enviando')}</>
                  ) : bonoUsable.es_membresia ? (
                    t('modal.btn_pagar_membresia', { nombre: bonoUsable.nombre })
                  ) : (
                    t('modal.btn_pagar_bono', { creditos: bonoUsable.creditos_restantes })
                  )}
                </button>
              )}
              <button
                type="submit"
                className={'modal-btn-principal ' + (bonoUsable ? 'btn-ghost modal-btn-secundario' : 'btn-primary')}
                disabled={estado === ESTADO_ENVIANDO}
              >
                {estado === ESTADO_ENVIANDO ? (
                  <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('modal.enviando')}</>
                ) : (
                  t('modal.btn_reservar_estudio')
                )}
              </button>
              <button
                type="button"
                className="btn-ghost modal-btn-secundario modal-btn-stripe"
                onClick={(e) => submit(e, 'online_stripe')}
                disabled={estado === ESTADO_ENVIANDO}
              >
                {t('modal.btn_pagar_online', { importe: importeTexto })}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// PantallaConfirmacion (vive dentro del modal)
// ============================================================================
function PantallaConfirmacion({ reserva, instancia, onClose, lang }) {
  const { t } = useTranslation();
  const codigo = formateaCodigoReserva(reserva.codigo_reserva);
  return (
    <div className="confirmacion">
      <div className="confirmacion-icono"><Check size={36} strokeWidth={2} /></div>
      <h2 className="confirmacion-titulo">{t('confirmacion.titulo')}</h2>

      <div className="confirmacion-codigo-wrap">
        <span className="confirmacion-codigo-label">{t('confirmacion.tu_codigo')}</span>
        <div className="confirmacion-codigo">{codigo}</div>
      </div>

      <div className="confirmacion-detalles">
        <div className="conf-linea conf-linea-fuerte">{instancia.clase_tipo.nombre}</div>
        <div className="conf-linea">
          {formateaFechaHora(instancia.fecha_clase, instancia.hora_inicio, lang)}
        </div>
        {instancia.profesor && (
          <div className="conf-linea conf-linea-sub">
            {t('reservas.con_profesor', { nombre: instancia.profesor.nombre_publico })}
          </div>
        )}
      </div>

      {reserva.bono && (
        <p className="confirmacion-bono">
          {reserva.bono.es_membresia
            ? t('confirmacion.bono_membresia')
            : t('confirmacion.bono_creditos', { creditos: reserva.bono.creditos_restantes })}
        </p>
      )}

      <p className="confirmacion-recordatorio">
        {reserva.estado === 'pendiente_confirmacion'
          ? t('confirmacion.recordatorio_bono')
          : t('confirmacion.recordatorio')}
      </p>

      <div className="confirmacion-acciones">
        <button type="button" className="btn-primary" onClick={onClose}>
          {t('confirmacion.btn_otra_clase')}
        </button>
        <Link
          to={`/reserva/${reserva.codigo_reserva}`}
          className="btn-ghost confirmacion-link-cancelar"
        >
          {t('confirmacion.btn_cancelar_reserva')}
        </Link>
      </div>
    </div>
  );
}

// ============================================================================
// Página /horarios
// ============================================================================
export default function Horarios() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();

  // mejora-web F3: ?dia=YYYY-MM-DD abre esa semana/día; ?reservar=<horario_id>
  // abre directamente el modal de reserva (enlace desde «Próximas clases»).
  const [searchParams, setSearchParams] = useSearchParams();
  const diaParam = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('dia') || '') ? searchParams.get('dia') : null;
  const reservarParam = searchParams.get('reservar');
  const [semana, setSemana] = useState(() => {
    if (!diaParam) return semanaActualISO();
    const d = parseFecha(diaParam);
    return { desde: aISO(lunesDe(d)), hasta: aISO(domingoDe(d)) };
  });
  const [diaSel, setDiaSel] = useState(diaParam);
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [horariosData, setHorariosData] = useState({ data: null, error: null, loading: true });
  const [estilos, setEstilos] = useState([]);
  const [instanciaModal, setInstanciaModal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // tick: fuerza re-render cada 60s para re-filtrar franjas que acaban de pasar.
  const [tick, setTick] = useState(0);
  // SS7.2.5: si el caller logado es profesor, fetch sus precios para mostrar
  // descuentos en cada tarjeta + bloquear reserva en clases donde es titular.
  const [preciosProfesor, setPreciosProfesor] = useState(null);  // Map clase_tipo_id → {pct, precio_publico, precio_profesor}
  const [profesorPropioId, setProfesorPropioId] = useState(null);

  // Fetch al cambiar semana / filtros / idioma / refresh
  useEffect(() => {
    let cancelled = false;
    setHorariosData((prev) => ({ data: prev.data, error: null, loading: true }));
    fetchHorarios({
      desde: semana.desde,
      hasta: semana.hasta,
      idioma: lang,
      estilo: filtros.estilo,
      formato: filtros.formato,
      nivel: filtros.nivel,
    })
      .then((json) => {
        if (cancelled) return;
        setHorariosData({ data: json.data || [], error: null, loading: false });
      })
      .catch((err) => {
        if (cancelled) return;
        setHorariosData({ data: null, error: err.message || 'error', loading: false });
      });
    return () => { cancelled = true; };
  }, [semana.desde, semana.hasta, lang, filtros.estilo, filtros.formato, filtros.nivel, refreshKey]);

  // mejora-web F2: opciones de Estilo desde la BD (solo clases con horario activo).
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/clases-tipos?idioma=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((json) => {
        if (cancelled) return;
        const lista = (json.data || [])
          .filter((c) => !c.duraciones_horario || c.duraciones_horario.length > 0)
          .map((c) => ({ valor: c.nombre_es || c.nombre, nombre: c.nombre }))
          .sort((a, b) => a.nombre.localeCompare(b.nombre, lang));
        setEstilos(lista);
      })
      .catch(() => { /* sin opciones de estilo: el resto del horario funciona */ });
    return () => { cancelled = true; };
  }, [lang]);

  // SS7.2.5: cargar precios profesor si el user logado es profesor.
  // Best-effort: si falla, la página sigue funcionando como alumno.
  useEffect(() => {
    if (!isAuthenticated || user?.rol !== 'profesor') {
      setPreciosProfesor(null);
      setProfesorPropioId(null);
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('idoyoga_auth_token');
    if (!token) return;
    getProfesorPrecios(token)
      .then((res) => {
        if (cancelled) return;
        const map = new Map();
        for (const c of res.data || []) {
          if (c.descuento_pct > 0) map.set(c.clase_tipo_id, c);
        }
        setPreciosProfesor(map);
        setProfesorPropioId(res.profesor_id || null);
      })
      .catch(() => { /* silencio */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.rol]);

  // Re-filtrar franjas pasadas cada minuto sin re-fetch.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const fechas = useMemo(() => fechasEntre(semana.desde, semana.hasta), [semana.desde, semana.hasta]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hoy = useMemo(() => ahoraEnMadrid().fecha, [tick]);

  // Oculta franjas cuyo inicio ya pasó (Europe/Madrid). `tick` fuerza
  // recálculo cada minuto sin re-fetch.
  const instanciasVisibles = useMemo(() => {
    const base = horariosData.data || [];
    // tick se referencia aquí para que el linter no la marque como no usada;
    // su único rol es invalidar el memo cuando pasa el tiempo.
    void tick;
    return base.filter((i) => !yaEmpezada(i.fecha_clase, i.hora_inicio));
  }, [horariosData.data, tick]);

  // Nº de clases por día (con filtros aplicados) para el selector de días.
  const conteo = useMemo(() => {
    const m = new Map();
    for (const i of instanciasVisibles) m.set(i.fecha_clase, (m.get(i.fecha_clase) || 0) + 1);
    return m;
  }, [instanciasVisibles]);

  // Día por defecto (si el usuario no ha elegido uno): el primer día desde hoy
  // que tenga clases; si la semana no tiene ninguna, hoy (o el primer día).
  const diaEfectivo = useMemo(() => {
    if (diaSel && fechas.includes(diaSel)) return diaSel;
    const conClases = fechas.find((f) => f >= hoy && (conteo.get(f) || 0) > 0);
    if (conClases) return conClases;
    return fechas.includes(hoy) ? hoy : fechas[0];
  }, [diaSel, fechas, conteo, hoy]);

  const instanciasDia = useMemo(
    () => instanciasVisibles.filter((i) => i.fecha_clase === diaEfectivo),
    [instanciasVisibles, diaEfectivo],
  );

  const handleReservar = useCallback((instancia) => {
    setInstanciaModal(instancia);
  }, []);

  // Apertura automática del modal desde ?reservar= (una sola vez).
  useEffect(() => {
    if (!reservarParam || !horariosData.data) return;
    const it = instanciasVisibles.find(
      (i) => i.horario_id === reservarParam && (!diaParam || i.fecha_clase === diaParam),
    );
    if (it && it.permite_reserva_online) setInstanciaModal(it);
    setSearchParams({}, { replace: true });
  }, [reservarParam, diaParam, horariosData.data, instanciasVisibles, setSearchParams]);
  const handleModalClose = useCallback(() => setInstanciaModal(null), []);
  const handleSuccess = useCallback(() => {
    setRefreshKey((k) => k + 1); // refetch para actualizar plazas
  }, []);
  const irASemanaActual = useCallback(() => {
    setSemana(semanaActualISO());
    setDiaSel(null);
  }, []);
  const cambiarSemana = useCallback((s) => { setSemana(s); setDiaSel(null); }, []);

  const algunFiltro = filtros.estilo.length > 0 || filtros.formato !== 'todos' || filtros.nivel !== 'todos';
  const sinResultados =
    horariosData.data && instanciasVisibles.length === 0 && algunFiltro;
  const cargaInicial = horariosData.loading && !horariosData.data;

  return (
    <main className="horarios-page">
      <header className="horarios-head">
        <span className="section-eyebrow">{t('reservas.eyebrow')}</span>
        <h1 className="horarios-titulo">{t('reservas.titulo')}</h1>
        <p className="horarios-sub">{t('reservas.sub')}</p>
        <SaldoBono />
        <Link to="/clase-particular" className="horarios-particular"><span>{stTxt(i18n.resolvedLanguage || 'es', 'cta')}</span><strong>→</strong></Link>
      </header>

      {/* mejora-web F2: barra fija compacta = semana + días; filtros debajo */}
      <div className="horarios-sticky">
        <SelectorSemana
          semana={semana}
          onChange={cambiarSemana}
          onActual={irASemanaActual}
        />
        <SelectorDias
          fechas={fechas}
          conteo={conteo}
          seleccionado={diaEfectivo}
          onSelect={setDiaSel}
          hoy={hoy}
        />
      </div>
      <div className="horarios-filtros-wrap">
        <FiltrosClases filtros={filtros} onChange={setFiltros} estilos={estilos} />
      </div>

      {horariosData.error ? (
        <div className="horarios-error">
          {t('reservas.error_carga')}
          <button className="clases-retry" onClick={() => setRefreshKey((k) => k + 1)}>
            {t('comun.reintentar')}
          </button>
        </div>
      ) : cargaInicial ? (
        <div className="horarios-skeleton">
          <section className="lista-dia">
            <div className="dia-heading dia-heading-skel" />
            <div className="filas-clase">
              {[0, 1, 2].map((i) => <div key={i} className="fila-clase fc-skel" />)}
            </div>
          </section>
        </div>
      ) : sinResultados ? (
        <div className="horarios-vacio">{t('reservas.sin_resultados')}</div>
      ) : (
        <div className={'lista-dias' + (horariosData.loading ? ' is-recargando' : '')}>
          <ListaDia
            fecha={diaEfectivo}
            instancias={instanciasDia}
            onReservar={handleReservar}
            preciosProfesor={preciosProfesor}
            profesorPropioId={profesorPropioId}
            hayFiltros={algunFiltro}
          />
        </div>
      )}

      {instanciaModal && (
        <ModalReserva
          instancia={instanciaModal}
          onClose={handleModalClose}
          onSuccess={handleSuccess}
        />
      )}
    </main>
  );
}
